// frontend/src/components/SphereMenu.js
import React, { useState, useRef, useEffect, useCallback, useMemo, Suspense } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Billboard, Text, Sphere, Environment } from "@react-three/drei";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { WoodMaterial } from "./materials/WoodMaterial";
import { useSphereTouchControls } from "../hooks/useSphereTouchControls";
import { useI18n } from "../i18n";
import "./SphereMenu.css";

// Helper for dynamic font size
function computeFontSize(text, maxSize = 0.35, _minSize = 0.18) {
  const str = text || '';
  if (str.length === 0) return maxSize;

  const words = str.split(' ');
  const maxWordLen = Math.max(...words.map(w => w.length));
  const totalLen = str.length;

  // Tight constraints for 70-char text fitting inside a sphere
  const MAX_WIDTH = 2.4;
  const MAX_HEIGHT = 2.4;
  const CHAR_WIDTH_RATIO = 0.55;
  const LINE_HEIGHT_FACTOR = 1.2;

  const sizeByWidth = MAX_WIDTH / (maxWordLen * CHAR_WIDTH_RATIO);
  const sizeByArea = Math.sqrt((MAX_HEIGHT * MAX_WIDTH) / (totalLen * CHAR_WIDTH_RATIO * LINE_HEIGHT_FACTOR));

  const calculated = Math.min(sizeByWidth, sizeByArea);
  const safeSize = calculated * 0.85;

  return Math.min(maxSize, Math.max(safeSize, _minSize));
}

// Hook to detect dark mode for color adjustments
function useIsDarkMode() {
  const [isDark, setIsDark] = useState(false);
  useEffect(() => {
    const check = () => {
       // Check data-theme attribute on html
       const val = document.documentElement.getAttribute('data-theme') === 'dark';
       setIsDark(val);
    };
    check();
    // Observer for changes
    const observer = new MutationObserver(check);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

// Spinning sphere component
const SpinningSphere = ({ radius, isLocked, onClick, onDoubleClick, mode, woodStyle, rotating }) => {
  const meshRef = useRef();
  const isDark = useIsDarkMode();

  useFrame(({ clock }, delta) => {
    if (meshRef.current && !isLocked) {
      // 1. Latido (Vida)
      const t = clock.getElapsedTime();
      const scale = 1 + Math.sin(t * 1.5) * 0.015;
      meshRef.current.scale.set(scale, scale, scale);

      // 2. Rotación
      if (rotating) {
        // ACTIVA: Giro Vertical (Eje X) estilo Rolodex
        meshRef.current.rotation.x += delta * 2.5;
      } else {
        // IDLE: Flotación suave
        meshRef.current.rotation.x += delta * 0.05;
        meshRef.current.rotation.y += delta * 0.02;
      }
    }
  });

  // Material helpers
  const getMaterial = () => {
    if (mode === 'classic') {
       return (
         <meshStandardMaterial
            color={isDark ? "#666" : "#ccc"}
            roughness={0.4}
            metalness={0.6}
            transparent
            opacity={0.8}
            wireframe={false}
         />
       );
    }
    if (mode === 'wood') {
        // We render this as a child of Sphere if we return null here,
        // OR we return the component. But standard Sphere expects a material or children.
        // WoodMaterial returns a <LayerMaterial> or <meshStandardMaterial>.
        // To use it properly inside <Sphere>, we can just render it.
        // Wait, the helper returns JSX.
        // We will call this helper in the children of Sphere.
        return null;
    }
    // Jade Stone
    return (
      <meshPhysicalMaterial
        color="#355E3B"
        roughness={0.15}
        metalness={0.05}
        clearcoat={1.0}
        clearcoatRoughness={0.1}
      />
    );
  };

  return (
    <Sphere
      ref={meshRef}
      args={[radius, 64, 64]}
      onClick={(e) => {
        e.stopPropagation();
        onClick && onClick(e);
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onDoubleClick && onDoubleClick(e);
      }}
    >
      {mode === 'wood' ? <WoodMaterial type={woodStyle} /> : getMaterial()}
    </Sphere>
  );
};

const SpinnerOverlay = ({ label = 'Cargando...' }) => (
  <div style={{
    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(255,255,255,0.5)', zIndex: 40, flexDirection: 'column', gap: 8, backdropFilter: 'blur(2px)'
  }}>
    <div style={{
      width: 40, height: 40, borderRadius: 20, border: '4px solid rgba(0,0,0,0.1)',
      borderTopColor: '#009688', animation: 'spin 1s linear infinite'
    }} />
    <div style={{ fontSize: 12, color: 'var(--text-color)', fontWeight: 'bold' }}>{label}</div>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

/**
 * options: [{ value, label, content?, route?, ... }]
 * onSelect(optionContentString) -> returns selected text
 * lockingEnabled: boolean - if true, allows locking via double click. If false, behaves like navigation (single click triggers action/route).
 */
const SphereMenu = ({ options = [], radius = 4, onSelect, onActiveIndexChange, disabled = false, loading = false, style = {}, lockingEnabled = true, rotating = false, initialIndex = 0, scale = 1, compact = false, onUserInteract }) => {
  const containerRef = useRef(null);
  const [containerNode, setContainerNode] = useState(null);
  const setContainerRef = useCallback((node) => {
    containerRef.current = node;
    setContainerNode(node);
  }, []);
  const navigate = useNavigate();
  const { t } = useI18n();
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const isDark = useIsDarkMode();

  useEffect(() => {
    if (onActiveIndexChange) {
      onActiveIndexChange(activeIndex);
    }
  }, [activeIndex, onActiveIndexChange]);

  const { user, woodStyle } = useAuth();
  const mode = user?.sphereMode || 'classic';

  // Fix reset bug: use deep comparison for options to avoid resetting on every render
  const optionsString = JSON.stringify(options);
  useEffect(() => {
    setActiveIndex(initialIndex);
    setSelectedIndex(null);
  }, [optionsString, initialIndex]);

  // Sync activeIndex with initialIndex changes from parent (Agent Q)
  useEffect(() => {
    setActiveIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    if (disabled) return;
    const handleWheel = (e) => {
      if (selectedIndex !== null) return;
      e.preventDefault();
      if (onUserInteract) onUserInteract();
      setActiveIndex((prev) =>
        e.deltaY > 0
          ? (prev < options.length - 1 ? prev + 1 : 0)
          : (prev > 0 ? prev - 1 : options.length - 1)
      );
    };
    const container = containerRef.current;
    container?.addEventListener("wheel", handleWheel, { passive: false });


    return () => {
      container?.removeEventListener("wheel", handleWheel);
    };
  }, [options.length, disabled, selectedIndex, onUserInteract]);

  const normalizedOptions = useMemo(() => options.map(o => {
    if (!o) return { value: '', label: '', content: '' };
    return { ...o, content: o.content ?? o.label ?? String(o.value ?? '') };
  }), [options]);

  const activeOption = normalizedOptions[activeIndex] || {};

  const handleInteraction = useCallback((type) => {
    if (disabled || loading) return;
    if (onUserInteract) onUserInteract();

    if (!lockingEnabled) {
       // Navigation mode (Main page): Simple click activates/navigates
       if (type === 'click') {
           const option = normalizedOptions[activeIndex];
           const route = typeof option.resolveRoute === 'function' ? option.resolveRoute(option) : option.route;
           if (route) {
               try { navigate(route); } catch (_) { /* ignore */ }
           }
           try { onSelect?.(activeOption.content ?? ''); } catch (_) {}
       }
       return;
    }

    // Locking mode (Conciencia/Rating):
    // Click -> Just centers/focuses (already done by wheel/dots, but nice to have)
    // Double Click -> Locks
    if (type === 'dblclick') {
        if (selectedIndex === activeIndex) {
            // Unlock if already locked (though UI usually handles this via overlay)
            setSelectedIndex(null);
            try { onSelect?.(null); } catch (_) {}
        } else {
            // Lock
            setSelectedIndex(activeIndex);
            try { onSelect?.(activeOption.content ?? ''); } catch (_) {}
        }
    }
  }, [activeIndex, activeOption.content, disabled, loading, lockingEnabled, navigate, normalizedOptions, onSelect, selectedIndex, onUserInteract]);

  const handleDoubleTap = useCallback(() => {
    handleInteraction('dblclick');
  }, [handleInteraction]);

  useSphereTouchControls(containerRef, options.length, disabled, selectedIndex, setActiveIndex, handleDoubleTap);

  if (!options.length) {
    if (loading) {
      return (
        <div style={{ position: 'relative', height: 220 }}>
          <SpinnerOverlay label={t('common.loading')} />
        </div>
      );
    }
    return null;
  }

  const fontSize = computeFontSize(
    activeOption.content || '',
    activeOption.fontSizeActive || 0.35,
    activeOption.fontSizeInactive || 0.2
  );

  const handleLockOverlayClick = () => {
    if (selectedIndex !== null) {
      setSelectedIndex(null);
      try { onSelect?.(null); } catch (_) {}
    }
  };

  const textColor = selectedIndex === activeIndex
    ? "#ffd700"
    : (mode === 'glass' || mode === 'wood' ? "#FFFFFF" : (isDark ? "#ffffff" : "#263238"));

  // Calculate camera distance based on radius to ensure the sphere fits inside the circular view.
  // With FOV 50, tan(25 deg) ~= 0.466.
  // To fit diameter 2*radius, distance > radius / 0.466 ~= 2.15 * radius.
  // We use 2.6 * radius to fit it tighter (bigger appearance) while avoiding clipping.
  const cameraZ = radius * 2.6;

  return (
    <div className={`sphere-menu-container sphere-${mode} ${selectedIndex !== null ? 'sphere-locked' : ''}`} ref={setContainerRef} style={{ position: 'relative', ...style }}>
      {loading && <SpinnerOverlay label={t('common.loading')} />}
      {containerNode && <Canvas
        camera={{ fov: 50, position: [0, 0, cameraZ] }}
        style={{ height: "100%", background: "transparent", pointerEvents: "auto" }}
        onDoubleClick={(e) => { e.preventDefault(); }} // Prevent canvas default dblclick text selection
      >
        <ambientLight intensity={isDark ? 0.7 : 1.0} />
        <pointLight position={[10, 10, 10]} intensity={1} />
        <pointLight position={[-10, -10, -10]} intensity={0.5} />
        <Suspense fallback={null}>
          <Environment files="/potsdamer_platz_1k.hdr" />
        </Suspense>

        <group scale={[scale, scale, scale]}>
          <SpinningSphere
            radius={radius}
            mode={mode}
            woodStyle={woodStyle}
            isLocked={selectedIndex !== null}
            onClick={() => handleInteraction('click')}
            onDoubleClick={() => handleInteraction('dblclick')}
            rotating={rotating}
          />

          <group position={[0, 0, radius + 0.2]}>
            <Billboard>
              <Text
                fontSize={fontSize}
                color={textColor}
                anchorX="center"
                anchorY="middle"
                maxWidth={2.5}
                textAlign="center"
                onClick={(e) => { e.stopPropagation(); handleInteraction('click'); }}
                onDoubleClick={(e) => { e.stopPropagation(); handleInteraction('dblclick'); }}
                style={{ cursor: (disabled || loading) ? "default" : "pointer" }}
              >
                {activeOption.content}
              </Text>
            </Billboard>
          </group>
        </group>
      </Canvas>}

      {/* Overlay UI - Candado dinámico (Top Center) */}
      {lockingEnabled && selectedIndex !== null && !loading && !disabled && (
        <div
          className="pointer-events-none"
          style={{
            zIndex: 10,
            position: 'absolute',
            top: compact ? '4%' : '12%', /* Zona segura superior - mas alta si es compacta */
            left: '50%',
            transform: 'translateX(-50%)',
            pointerEvents: 'none'
          }}
        >
          <div
            style={{
              background: 'rgba(0,0,0,0.5)',
              borderRadius: '50%',
              width: compact ? '22px' : '40px',
              height: compact ? '22px' : '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(2px)',
              boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
              cursor: 'pointer',
              pointerEvents: 'auto'
            }}
            data-sphere-touch-ignore
            onClick={handleLockOverlayClick}
            title={t('consciousness.unlockClick')}
          >
            <svg width={compact ? "10" : "18"} height={compact ? "10" : "18"} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
          </div>
        </div>
      )}

      {/* Dots navigation */}
      <div className="sphere-menu-dots" aria-hidden>
        {normalizedOptions.map((opt, i) => (
          <div
            key={i}
            role="button"
            data-option-text={opt.content}
            className={`dot ${i === activeIndex ? "active bg-gray-900 dark:bg-white" : "bg-gray-400 dark:bg-gray-600"} ${i === selectedIndex ? "selected" : ""}`}
            onClick={() => {
              if (disabled || loading) return;
              if (onUserInteract) onUserInteract();
              if (selectedIndex === i) {
                // Unlock if clicked the selected dot
                setSelectedIndex(null);
                try { onSelect?.(null); } catch (_) {}
                return;
              }

              setActiveIndex(i);

              if (lockingEnabled) {
                // En modo Conciencia/Rating, el click en el punto también selecciona/bloquea
                setSelectedIndex(i);
                try { onSelect?.(opt.content ?? ''); } catch (_) {}
              } else {
                // Modo navegación (Página principal)
                if (normalizedOptions[i].route) {
                   try { navigate(normalizedOptions[i].route); } catch (_) { /* ignore */ }
                }
                try { onSelect?.(normalizedOptions[i].content ?? ''); } catch (_) {}
              }
            }}
            title={opt.content}
          />
        ))}
      </div>

       {/* Hint text for locking */}
       {lockingEnabled && selectedIndex === null && !loading && !disabled && (
        <div
          className="text-center text-xs text-gray-500 dark:text-gray-400 mt-2 pointer-events-none select-none"
          style={{
            fontSize: '0.8rem',
            color: 'var(--text-secondary, #888)',
            marginTop: '8px',
            pointerEvents: 'none',
            userSelect: 'none'
        }}>
          {t('consciousness.doubleClickLock')}
        </div>
      )}
    </div>
  );
};

export default SphereMenu;
